import { defineStore } from 'pinia'
import { ref, shallowRef, computed } from 'vue'

import { useSiteConfigStore } from './siteConfig'

import { resolveReferralAc, resolveReferralFc, resolveReferralKpix, resolveReferralPix } from '@/utils/referral'

const MAX_TOAST_SIZE = 30

export const useMainStore = defineStore('main', () => {
  // 延遲取得 siteConfigStore，避免在 Pinia 初始化前調用
  const getSiteConfigStore = () => useSiteConfigStore()

  // State (使用 ref)
  const loading = ref({})
  const downloadGuide = ref(false)
  const modalLoading = ref(false)
  const loadingGame = ref(false)
  const modal = ref(null)
  const modalParams = ref(null)
  const systemModal = ref(null)
  const systemModalParams = ref(null)
  // 網址觸發（/register、/login、?modal=）待處理的意圖；為真時公告延後顯示
  // 由 middleware/url-modal.global.js 於路由階段設定，早於任何元件 mount，
  // 避免與 fetchAnnouncements() 形成 race
  const urlModalPending = ref(null)
  // 被延後的公告清單（已通過既有篩選），彈窗關閉後補跳
  const deferredAnnouncement = ref(null)
  const verifyModal = ref(null)
  const bindBankStrategy = ref(null)
  const bindBankCheckpoint = ref(null)
  const showBindBank = ref(false)
  const bindBankModal = ref(null)

  // Promise resolver（用於等待銀行綁定完成）
  let bindBankResolver = null
  const errorMessage = ref(null)
  const errorModal = ref(null)
  const animationInitData = ref(null)

  // Global info
  const jackpotList = ref([])
  const turnoverLimitTip = ref(false)
  const withdrawRule = ref({})
  const promotion = ref(null)
  const browserAgent = ref(null)
  // 存放宿主物件 window.navigator：用 shallowRef 避免深層 reactive 代理。
  // 深層代理會呼叫 Object.prototype.toString.call(navigator) 判定型別，在 navigator
  // 被注入腳本包成 Proxy 的環境下丟出 TypeError: Cannot convert a Symbol value to a string (CLIENT-PWA-SSR-M4)
  const navigator = shallowRef({})
  const trackLocation = ref(null)
  const storage = ref(null)
  const customerServiceModal = ref(null)
  const playingGame = ref(null)
  const jackpotWinning = ref({})
  const toast = ref([])
  const gameSearchPageEnable = ref(false)
  const connectSocket = ref(0)
  const providerInfo = ref([])
  const freeGameList = ref([])
  const telegramUserData = ref(null)
  const updateMessage = ref(false)
  const normalBroadcast = ref(null)
  const shareGame = ref(null)
  const iframeUrl = ref(null)

  // Getters (只保留有計算邏輯的 computed，冗餘的 getter 改為直接引用 ref)
  const isWebView = computed(() => {
    return browserAgent.value?.ua?.includes('th1games')
  })

  const isAndroid = computed(() => {
    return navigator.value.userAgent?.indexOf('Android') > -1 || navigator.value.userAgent?.indexOf('Adr') > -1
  })

  const isIOS = computed(() => {
    return browserAgent.value?.os?.name === 'iOS'
  })

  const isInStandaloneMode = computed(() => {
    return 'standalone' in navigator.value && navigator.value.standalone
  })

  const downloadVisible = computed(() => {
    return !isWebView.value && !isInStandaloneMode.value && (isAndroid.value || isIOS.value)
  })

  const isTablet = computed(() => {
    const userAgent = navigator.value.userAgent.toLowerCase()
    const isAndroidTablet = /android/.test(userAgent) && !/mobile/.test(userAgent)
    const isIPad = /ipad/.test(userAgent)
    return isAndroidTablet || isIPad
  })

  const isDesktop = computed(() => {
    return !isWebView.value && !isInStandaloneMode.value && !isAndroid.value && !isIOS.value
  })

  const iOSversion = computed(() => {
    return browserAgent.value?.os?.version
  })

  const getDownloadLink = computed(() => {
    const parameterObj = {
      ac: resolveReferralAc(),
      fc: resolveReferralFc(),
      pix: resolveReferralPix(),
      kpix: resolveReferralKpix(),
    }
    return getSiteConfigStore().getDownloadLink(parameterObj)
  })

  const getLoading = computed(() => {
    return Object.values(loading.value).some((v) => v)
  })

  const getJackpotList = computed(() => jackpotList.value || [])

  const isBRLCurrency = computed(() => {
    return (process.env.CURRENCY_SYMBOL || '') === 'R$'
  })

  const getConnectSocket = computed(() => {
    return connectSocket.value === 2
  })

  // Actions (使用普通函數)
  function setFreeGameList(data) {
    freeGameList.value = data
  }

  function setProviderInfo(data) {
    providerInfo.value = data
  }

  function setDownloadGuide(data) {
    downloadGuide.value = data
  }

  function setLoading(data) {
    loading.value = { ...loading.value, ...data }
  }

  function setModalLoading(data) {
    modalLoading.value = data
  }

  function setLoadingGame(data) {
    loadingGame.value = data
  }

  function setModal(data) {
    modal.value = data
    if (data === null || data === undefined) {
      modalParams.value = null
    }
  }

  function setModalParams(data) {
    modalParams.value = data
  }

  function setShareGame(data) {
    shareGame.value = data
  }

  function setSystemModal(data) {
    systemModal.value = data
    if (data === null || data === undefined) {
      systemModalParams.value = null
    }
  }

  function setSystemModalParams(data) {
    systemModalParams.value = data
  }

  function setUrlModalPending(data) {
    urlModalPending.value = data
  }

  function setDeferredAnnouncement(data) {
    deferredAnnouncement.value = data
  }

  function closeModal(data) {
    if (data === modal.value) {
      modal.value = null
    }
  }

  // 關閉所有 Modal（保留 error modal）
  function closeAllModals() {
    modal.value = null
    modalParams.value = null
    systemModal.value = null
    systemModalParams.value = null
    verifyModal.value = null
    bindBankModal.value = null
    customerServiceModal.value = null
  }

  // 依白名單關閉不屬於當前作用域的彈窗（保留 error modal）
  // verifyModal / bindBankModal / customerServiceModal 刻意不清：三者皆為登入／註冊的
  // 必經彈窗，且 bindBankResolver 等 Promise resolver 由彈窗自身呼叫，清狀態會讓流程懸掛
  function closeModalsExcept(allowList = []) {
    // modal.value 為 null 時不動 modalParams：GuestLogin.vue 的 setModal(null) → nextTick →
    // setModalParams() → setModal() 正處於這個時窗，清掉會讓彈窗以空 params 開啟
    if (modal.value && !allowList.includes(modal.value)) {
      modal.value = null
      modalParams.value = null
    }
    systemModal.value = null
    systemModalParams.value = null
    deferredAnnouncement.value = null
    urlModalPending.value = null
  }

  function setVerifyModal(data) {
    verifyModal.value = data
  }

  function setBindBankStrategy(data) {
    bindBankStrategy.value = data
  }

  function setBindBankCheckpoint(data) {
    bindBankCheckpoint.value = data
  }

  function setShowBindBank(data) {
    showBindBank.value = data
  }

  function setBindBankModal(data) {
    bindBankModal.value = data
  }

  /**
   * 設定銀行綁定 Promise resolver
   */
  function setBindBankResolver(resolver) {
    bindBankResolver = resolver
  }

  /**
   * 解決銀行綁定 Promise
   * @param {boolean} success - 綁定是否成功
   */
  function resolveBindBank(success) {
    if (bindBankResolver) {
      bindBankResolver(success)
      bindBankResolver = null
    }
  }

  function setErrorMessage(data) {
    errorMessage.value = data
  }

  function setErrorModal(data) {
    errorModal.value = data
  }

  function setAnimationInitData(data) {
    animationInitData.value = data
  }

  function setJackpotList(data) {
    jackpotList.value = data
  }

  const setTurnoverLimitTip = (data) => {
    turnoverLimitTip.value = data
  }

  function setWithdrawRule(data) {
    withdrawRule.value = data
  }

  function setPromotion(data) {
    promotion.value = data
  }

  function setBrowserAgent(data) {
    browserAgent.value = data
  }

  function setNavigator(data) {
    navigator.value = data
  }

  function setTrackLocation(data) {
    trackLocation.value = data
  }

  function setStorage(data) {
    storage.value = data
  }

  function setCustomrerServiceModal(data) {
    customerServiceModal.value = data
  }

  function setPlayingGame(data) {
    playingGame.value = data
  }

  function setJackpotWinning(data) {
    jackpotWinning.value = data
  }

  function setToast(data) {
    toast.value = data
  }

  function pushToast(data) {
    toast.value = [...toast.value, data]
    if (toast.value.length > MAX_TOAST_SIZE) {
      toast.value = toast.value.slice(-MAX_TOAST_SIZE)
    }
  }

  function setGameSearchPageEnable(data) {
    gameSearchPageEnable.value = data
  }

  function setConnectSocket(data) {
    connectSocket.value = data
  }

  function setTelegramUserData(data) {
    telegramUserData.value = data
  }

  function setUpdateMessage(data) {
    updateMessage.value = data
  }

  function setNormalBroadcast(data) {
    normalBroadcast.value = data
  }

  function setIframeUrl(data) {
    iframeUrl.value = data
  }

  // Return everything to make it available
  return {
    // State
    loading,
    downloadGuide,
    modalLoading,
    loadingGame,
    modal,
    modalParams,
    systemModal,
    systemModalParams,
    urlModalPending,
    deferredAnnouncement,
    verifyModal,
    bindBankStrategy,
    bindBankCheckpoint,
    showBindBank,
    bindBankModal,
    errorMessage,
    errorModal,
    animationInitData,
    jackpotList,
    turnoverLimitTip,
    withdrawRule,
    promotion,
    browserAgent,
    navigator,
    trackLocation,
    storage,
    customerServiceModal,
    playingGame,
    jackpotWinning,
    toast,
    gameSearchPageEnable,
    connectSocket,
    providerInfo,
    freeGameList,
    telegramUserData,
    updateMessage,
    normalBroadcast,
    shareGame,
    iframeUrl,

    // Getters (有計算邏輯的 computed)
    isWebView,
    isAndroid,
    isIOS,
    isInStandaloneMode,
    downloadVisible,
    isTablet,
    isDesktop,
    iOSversion,
    getDownloadLink,
    getLoading,
    getJackpotList,
    isBRLCurrency,
    getConnectSocket,

    // Getter aliases (直接引用 ref，向後相容)
    getFreeGameList: freeGameList,
    getProviderInfo: providerInfo,
    getModalLoading: modalLoading,
    getLoadingGame: loadingGame,
    getModal: modal,
    getModalParams: modalParams,
    getShareGame: shareGame,
    getSystemModal: systemModal,
    getSystemModalParams: systemModalParams,
    getUrlModalPending: urlModalPending,
    getDeferredAnnouncement: deferredAnnouncement,
    getVerifyModal: verifyModal,
    getBindBankStrategy: bindBankStrategy,
    getBindBankCheckpoint: bindBankCheckpoint,
    getShowBindBank: showBindBank,
    getBindBankModal: bindBankModal,
    getErrorMessage: errorMessage,
    getErrorModal: errorModal,
    getAnimationInitData: animationInitData,
    getTrackLocation: trackLocation,
    isGuiding: downloadGuide,
    getTurnoverLimitTip: turnoverLimitTip,
    getWithdrawRule: withdrawRule,
    getPromotion: promotion,
    getCustomerServiceModal: customerServiceModal,
    getPlayingGame: playingGame,
    getJackpotWinning: jackpotWinning,
    getToast: toast,
    getGameSearchPageEnable: gameSearchPageEnable,
    getTelegramUserData: telegramUserData,
    getUpdateMessage: updateMessage,
    getNormalBroadcast: normalBroadcast,

    // Actions
    setFreeGameList,
    setProviderInfo,
    setDownloadGuide,
    setLoading,
    setModalLoading,
    setLoadingGame,
    setModal,
    setModalParams,
    setShareGame,
    setSystemModal,
    setSystemModalParams,
    setUrlModalPending,
    setDeferredAnnouncement,
    closeModal,
    closeAllModals,
    closeModalsExcept,
    setVerifyModal,
    setBindBankStrategy,
    setBindBankCheckpoint,
    setShowBindBank,
    setBindBankModal,
    setBindBankResolver,
    resolveBindBank,
    setErrorMessage,
    setErrorModal,
    setAnimationInitData,
    setJackpotList,
    setTurnoverLimitTip,
    setWithdrawRule,
    setPromotion,
    setBrowserAgent,
    setNavigator,
    setTrackLocation,
    setStorage,
    setCustomrerServiceModal,
    setPlayingGame,
    setJackpotWinning,
    setToast,
    pushToast,
    setGameSearchPageEnable,
    setConnectSocket,
    setTelegramUserData,
    setUpdateMessage,
    setNormalBroadcast,
    setIframeUrl,
  }
})
